import React, { useState, useEffect } from "react";
import { MotiView } from "moti";
import { View, StyleSheet } from "react-native";
import { useRoute, RouteProp, useNavigation, NavigationProp } from "@react-navigation/native";
import type { RootStackParamList } from "../../navigation/navigation.types";

import { useAppConfig } from "../../AppConfigProvider";
import usePagination from "../../hooks/usePagination";
import useSortingDropdown, { SORT_ITEMS } from "../../hooks/useSortingDropdown";
import { useAnalyticsData } from "../../hooks/useAnalyticsData";
import { Course, Degree, DegreesBySchoolByQueryData } from "../../graphql/generated/graphql";
import AdminLayout from "../../layouts/AdminLayout.web";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomSearchInput from "../../components/common/general/CustomSearchInput/CustomSearchInput";
import SortingDropdown from "../../components/common/TableControls/SortingDropdown/SortingDropdown";
import TabNavigation from "../../components/adminDashboard/Analytics/list/TabNavigation";
import DegreesList from "../../components/adminDashboard/Analytics/list/DegreesList";
import DegreeDetail from "../../components/adminDashboard/Analytics/detail/DegreeDetail";
import CoursesList from "../../components/adminDashboard/Analytics/list/CoursesList";
import CourseDetail from "../../components/adminDashboard/Analytics/detail/CourseDetail";
import StudentsList from "../../components/adminDashboard/Analytics/list/StudentsList";
import StudentDetail from "../../components/adminDashboard/Analytics/detail/StudentDetail";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";

function AdminAnalyticsView() {
  const { hasDegrees } = useAppConfig();
  const route = useRoute<RouteProp<RootStackParamList, "Admin Analytics">>();
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const { studentId } = route.params || {};

  const [activeAnalyticsParams, setActiveAnalyticsParams] = useState<{
    entityType: "degree" | "course" | "student";
    entityId: string | null;
    entityGroupName: string;
  }>({
    entityType: studentId ? "student" : hasDegrees ? "degree" : "course",
    entityId: studentId || null,
    entityGroupName: studentId ? "Students" : hasDegrees ? "Degree Programs" : "Courses",
  });

  const [searchQuery, setSearchQuery] = useState("");
  const { page, setPage, pageSize, setPageSize } = usePagination({ itemsPerPage: 10 });
  const { sortBy, setSortBy, sortDropdownOpen, setSortDropdownOpen } = useSortingDropdown({
    defaultSortBy: "date_desc",
  });

  const {
    coursesData,
    studentsData,
    completionData,
    detailedCourse,
    degreesData,
    coursesByDegreeData,
    studentDetailedAnalyticsData,
    setCourseIdForDetailedCourse,
    setDegreeIdForDetailedDegree,
    setStudentIdForDetailedStudent,
    isLoading,
    studentDetailedAnalyticsLoading,
    refetchData,
  } = useAnalyticsData({
    searchQuery,
    page,
    pageSize,
    sortBy,
  });

  useEffect(() => {
    refetchData();
  }, [searchQuery, page, pageSize, sortBy]);

  useEffect(() => {
    if (studentId) {
      if (!activeAnalyticsParams.entityId || studentId !== activeAnalyticsParams.entityId) {
        handleEntitySelect("student", studentId, "Students");
      }
    }
  }, [studentId]);

  useEffect(() => {
    if (activeAnalyticsParams.entityId && activeAnalyticsParams.entityType === "course") {
      setCourseIdForDetailedCourse(activeAnalyticsParams.entityId);
    }
    if (activeAnalyticsParams.entityId && activeAnalyticsParams.entityType === "degree") {
      setDegreeIdForDetailedDegree(activeAnalyticsParams.entityId);
    }
    if (activeAnalyticsParams.entityId && activeAnalyticsParams.entityType === "student") {
      setStudentIdForDetailedStudent(activeAnalyticsParams.entityId);
    }
  }, [activeAnalyticsParams.entityId, activeAnalyticsParams.entityType]);

  const handleEntitySelect = (
    entityType: "degree" | "course" | "student",
    entityId: string,
    entityGroupName: string
  ) => {
    if (!entityId) {
      setSearchQuery("");
      setPage(1);
    }
    setSortBy("date_desc");
    setSortDropdownOpen(false);
    setActiveAnalyticsParams({ entityType, entityId, entityGroupName });
  };

  const handleBackToStudents = () => {
    handleEntitySelect("student", "", "Students");
    navigation.setParams({ studentId: undefined });
  };

  const handleStudentSelect = (studentId: string) => {
    handleEntitySelect("student", studentId, "Students");
    navigation.setParams({ studentId });
  };

  return (
    <AdminLayout>
      <MotiView
        from={{ opacity: 0, translateY: 20 }}
        animate={{ opacity: 1, translateY: 0 }}
        delay={10}
        // @ts-expect-error - transition is not typed
        transition={{ type: "timing", duration: 500 }}>
        <CustomText textType="display" text="Analytics" weight="ultraBold" />
      </MotiView>

      <View style={styles.container}>
        {!activeAnalyticsParams.entityId && (
          <TabNavigation
            activeEntityType={activeAnalyticsParams.entityType}
            hasDegrees={hasDegrees}
            onEntitySelect={handleEntitySelect}
          />
        )}

        <View style={styles.tabContent}>
          <View style={styles.tabHeader}>
            <CustomText text={activeAnalyticsParams.entityGroupName} size="xl" weight="bold" />
          </View>

          <CustomSpacing type="vertical" size="m" />

          <View style={styles.controlsContainer}>
            <CustomSearchInput searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
            <SortingDropdown
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortDropdownOpen={sortDropdownOpen}
              setSortDropdownOpen={setSortDropdownOpen}
              sortItems={SORT_ITEMS}
              label="Sort by"
            />
          </View>

          <CustomSpacing type="vertical" size="m" />

          {activeAnalyticsParams.entityType === "degree" && (
            <>
              {activeAnalyticsParams.entityId ? (
                <DegreeDetail
                  degreeId={activeAnalyticsParams.entityId}
                  onBackToDegrees={() => handleEntitySelect("degree", "", "Degree Programs")}
                  onCourseSelect={(courseId) => handleEntitySelect("course", courseId, "Courses")}
                  degreesData={degreesData?.degrees as Degree[]}
                  coursesByDegreeData={coursesByDegreeData?.courses as Course[]}
                  loading={isLoading}
                />
              ) : (
                <DegreesList
                  degreesData={degreesData as DegreesBySchoolByQueryData}
                  onDegreeSelect={(degreeId) => handleEntitySelect("degree", degreeId, "Degree Programs")}
                  isLoading={isLoading}
                  page={page}
                  setPage={setPage}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                />
              )}
            </>
          )}

          {activeAnalyticsParams.entityType === "course" && (
            <>
              {activeAnalyticsParams.entityId ? (
                <CourseDetail
                  courseId={activeAnalyticsParams.entityId}
                  onBackToCourses={() => handleEntitySelect("course", "", "Courses")}
                  completionData={completionData?.data}
                  detailedCourse={detailedCourse}
                  loading={isLoading}
                />
              ) : (
                <CoursesList
                  coursesData={coursesData}
                  onCourseSelect={(courseId) => handleEntitySelect("course", courseId, "Courses")}
                  page={page}
                  setPage={setPage}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                />
              )}
            </>
          )}

          {activeAnalyticsParams.entityType === "student" && (
            <>
              {activeAnalyticsParams.entityId ? (
                <StudentDetail
                  studentDetailedAnalyticsData={studentDetailedAnalyticsData}
                  onBackToStudents={handleBackToStudents}
                  loading={studentDetailedAnalyticsLoading}
                />
              ) : (
                <StudentsList
                  studentsData={studentsData}
                  onStudentSelect={handleStudentSelect}
                  page={page}
                  setPage={setPage}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                />
              )}
            </>
          )}
        </View>
      </View>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingVertical: 16,
  },
  tabContent: {
    flex: 1,
  },
  tabHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  controlsContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    justifyContent: "flex-start",
    width: "100%",
    zIndex: 3000,
  },
});

export default AdminAnalyticsView;
